'use client'

import { useMemo, type ReactNode } from 'react'
import { Input } from '@/components/ui/input'

interface StyleEditorProps {
  value: string | undefined
  onChange: (value: string) => void
}

type FieldConfig = { key: string; label: string }

type SideFieldConfig = {
  key: string
  side: 'T' | 'R' | 'B' | 'L'
  full: string
}

const SIZE_FIELDS: FieldConfig[] = [
  { key: 'minHeight', label: 'minHeight' },
  { key: 'maxHeight', label: 'maxHeight' },
  { key: 'minWidth', label: 'minWidth' },
  { key: 'maxWidth', label: 'maxWidth' },
]

const PADDING_SIDE_FIELDS: SideFieldConfig[] = [
  { key: 'paddingTop', side: 'T', full: 'paddingTop' },
  { key: 'paddingRight', side: 'R', full: 'paddingRight' },
  { key: 'paddingBottom', side: 'B', full: 'paddingBottom' },
  { key: 'paddingLeft', side: 'L', full: 'paddingLeft' },
]

const MARGIN_SIDE_FIELDS: SideFieldConfig[] = [
  { key: 'marginTop', side: 'T', full: 'marginTop' },
  { key: 'marginRight', side: 'R', full: 'marginRight' },
  { key: 'marginBottom', side: 'B', full: 'marginBottom' },
  { key: 'marginLeft', side: 'L', full: 'marginLeft' },
]

// Same four sides, but scoped to `extra_css.text` (the nested text-only style
// object), so they're kept as a distinct config from the top-level padding
// fields above even though the underlying key names are identical.
const TEXT_PADDING_SIDE_FIELDS: SideFieldConfig[] = [
  { key: 'paddingTop', side: 'T', full: 'text.paddingTop' },
  { key: 'paddingRight', side: 'R', full: 'text.paddingRight' },
  { key: 'paddingBottom', side: 'B', full: 'text.paddingBottom' },
  { key: 'paddingLeft', side: 'L', full: 'text.paddingLeft' },
]

const COLOR_FIELDS: FieldConfig[] = [
  { key: 'backgroundColor', label: 'backgroundColor' },
  { key: 'color', label: 'color' },
]

const TEXT_COLOR_FIELDS: FieldConfig[] = [
  { key: 'color', label: 'color' },
  { key: 'backgroundColor', label: 'backgroundColor' },
]

const NUMBER_KEYS = new Set([
  ...SIZE_FIELDS.map(f => f.key),
  ...PADDING_SIDE_FIELDS.map(f => f.key),
  ...MARGIN_SIDE_FIELDS.map(f => f.key),
  'borderRadius',
  'opacity',
])

const STRING_KEYS = new Set([...COLOR_FIELDS.map(f => f.key), 'boxShadow'])

// Top-level known flat keys. NOTE: the old shorthand `padding`/`margin` keys
// are intentionally NOT included here (per-side fields replace them in the
// UI) — any stored shorthand value now falls into the `preserved` bucket
// below and is carried through on save untouched instead of being dropped.
const KNOWN_KEYS = new Set<string>([...NUMBER_KEYS, ...STRING_KEYS])

// Known keys inside the nested `text` object.
const TEXT_NUMBER_KEYS = new Set([
  ...TEXT_PADDING_SIDE_FIELDS.map(f => f.key),
  'borderRadius',
])

const TEXT_STRING_KEYS = new Set([...TEXT_COLOR_FIELDS.map(f => f.key)])

const TEXT_KNOWN_KEYS = new Set<string>([
  ...TEXT_NUMBER_KEYS,
  ...TEXT_STRING_KEYS,
])

function parseValue(value: string | undefined): Record<string, unknown> {
  if (!value || value.trim() === '') return {}
  try {
    const parsed = JSON.parse(value)
    return typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}

function toHexOrDefault(value: unknown): string {
  if (typeof value === 'string') {
    if (/^#[0-9a-fA-F]{6}$/.test(value)) return value
    if (/^#[0-9a-fA-F]{3}$/.test(value)) {
      return (
        '#' +
        value
          .slice(1)
          .split('')
          .map(c => c + c)
          .join('')
      )
    }
  }
  return '#000000'
}

// A single "label on the left, input on the right" row used throughout the
// editor for simple (non-per-side) fields: full CSS property name as the
// label, left-aligned, followed by one or more compact inputs.
function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-36 shrink-0 text-left text-xs text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  )
}

// Renders one group's per-side (T/R/B/L) number inputs on a single row, with
// the side letter shown as a caption underneath each box rather than inside
// it (so the box never looks like it already has a value).
function SideInputsRow({
  fields,
  getValue,
  onValueChange,
}: {
  fields: SideFieldConfig[]
  getValue: (key: string) => number | ''
  onValueChange: (key: string, raw: string) => void
}) {
  return (
    <div className="flex items-center gap-2">
      {fields.map(f => (
        <div key={f.key} className="flex flex-col items-center gap-1">
          <Input
            type="number"
            aria-label={f.full}
            value={getValue(f.key)}
            onChange={e => onValueChange(f.key, e.target.value)}
            className="h-7 w-16 text-center text-xs"
          />
          <span className="text-[10px] text-muted-foreground" title={f.full}>
            {f.side}
          </span>
        </div>
      ))}
    </div>
  )
}

export function StyleEditor({ value, onChange }: StyleEditorProps) {
  const parsed = useMemo(() => parseValue(value), [value])

  const { fields, preserved, textFields, textPreserved } = useMemo(() => {
    const fields: Record<string, unknown> = {}
    const preserved: Record<string, unknown> = {}
    const textFields: Record<string, unknown> = {}
    const textPreserved: Record<string, unknown> = {}

    Object.entries(parsed).forEach(([key, val]) => {
      if (key === 'text') {
        if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
          Object.entries(val as Record<string, unknown>).forEach(
            ([tKey, tVal]) => {
              if (TEXT_KNOWN_KEYS.has(tKey)) {
                textFields[tKey] = tVal
              } else {
                textPreserved[tKey] = tVal
              }
            }
          )
        } else {
          // Malformed/legacy `text` value that isn't an object — keep it
          // untouched at the top level instead of dropping it.
          preserved[key] = val
        }
        return
      }
      if (KNOWN_KEYS.has(key)) {
        fields[key] = val
      } else {
        preserved[key] = val
      }
    })

    return { fields, preserved, textFields, textPreserved }
  }, [parsed])

  const emit = (
    nextFields: Record<string, unknown>,
    nextTextFields: Record<string, unknown>
  ) => {
    const isEmpty = (val: unknown) =>
      val === undefined ||
      val === null ||
      val === '' ||
      (typeof val === 'number' && Number.isNaN(val))

    const result: Record<string, unknown> = { ...preserved }
    Object.entries(nextFields).forEach(([key, val]) => {
      if (isEmpty(val)) return
      result[key] = val
    })

    const textObj: Record<string, unknown> = { ...textPreserved }
    Object.entries(nextTextFields).forEach(([key, val]) => {
      if (isEmpty(val)) return
      textObj[key] = val
    })

    if (Object.keys(textObj).length > 0) {
      result.text = textObj
    }

    onChange(Object.keys(result).length > 0 ? JSON.stringify(result) : '')
  }

  const handleNumberChange = (key: string, raw: string) => {
    const next = { ...fields }
    if (raw === '') {
      delete next[key]
    } else {
      const num = parseFloat(raw)
      if (Number.isNaN(num)) {
        delete next[key]
      } else {
        next[key] = num
      }
    }
    emit(next, textFields)
  }

  const handleStringChange = (key: string, raw: string) => {
    const next = { ...fields }
    if (raw === '') {
      delete next[key]
    } else {
      next[key] = raw
    }
    emit(next, textFields)
  }

  const handleTextNumberChange = (key: string, raw: string) => {
    const next = { ...textFields }
    if (raw === '') {
      delete next[key]
    } else {
      const num = parseFloat(raw)
      if (Number.isNaN(num)) {
        delete next[key]
      } else {
        next[key] = num
      }
    }
    emit(fields, next)
  }

  const handleTextStringChange = (key: string, raw: string) => {
    const next = { ...textFields }
    if (raw === '') {
      delete next[key]
    } else {
      next[key] = raw
    }
    emit(fields, next)
  }

  const numberValue = (key: string) => {
    const val = fields[key]
    return typeof val === 'number' ? val : ''
  }

  const stringValue = (key: string) => {
    const val = fields[key]
    return typeof val === 'string' ? val : ''
  }

  const textNumberValue = (key: string) => {
    const val = textFields[key]
    return typeof val === 'number' ? val : ''
  }

  const textStringValue = (key: string) => {
    const val = textFields[key]
    return typeof val === 'string' ? val : ''
  }

  return (
    <div className="space-y-4">
      <div>
        <h4 className="mb-2 text-xs font-medium text-muted-foreground">Size</h4>
        <div className="space-y-2">
          {SIZE_FIELDS.map(f => (
            <FieldRow key={f.key} label={f.label}>
              <Input
                type="number"
                value={numberValue(f.key)}
                onChange={e => handleNumberChange(f.key, e.target.value)}
                className="h-7 w-40 px-2 text-xs"
              />
            </FieldRow>
          ))}
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-xs font-medium text-muted-foreground">
          Padding{' '}
          <span className="font-normal text-muted-foreground/70">(px)</span>
        </h4>
        <SideInputsRow
          fields={PADDING_SIDE_FIELDS}
          getValue={numberValue}
          onValueChange={handleNumberChange}
        />
      </div>

      <div>
        <h4 className="mb-2 text-xs font-medium text-muted-foreground">
          Margin{' '}
          <span className="font-normal text-muted-foreground/70">(px)</span>
        </h4>
        <SideInputsRow
          fields={MARGIN_SIDE_FIELDS}
          getValue={numberValue}
          onValueChange={handleNumberChange}
        />
      </div>

      <div>
        <h4 className="mb-2 text-xs font-medium text-muted-foreground">
          Appearance
        </h4>
        <div className="space-y-2">
          {COLOR_FIELDS.map(f => (
            <FieldRow key={f.key} label={f.label}>
              <input
                type="color"
                aria-label={`${f.label} swatch`}
                value={toHexOrDefault(fields[f.key])}
                onChange={e => handleStringChange(f.key, e.target.value)}
                className="h-7 w-12 shrink-0 cursor-pointer rounded border border-input p-0.5"
              />
              <Input
                type="text"
                value={stringValue(f.key)}
                onChange={e => handleStringChange(f.key, e.target.value)}
                placeholder="unset"
                className="h-7 w-[6.5rem] px-2 text-xs"
              />
            </FieldRow>
          ))}

          <FieldRow label="borderRadius">
            <Input
              type="number"
              value={numberValue('borderRadius')}
              onChange={e => handleNumberChange('borderRadius', e.target.value)}
              className="h-7 w-40 px-2 text-xs"
            />
          </FieldRow>

          <FieldRow label="opacity">
            <Input
              type="number"
              step={0.1}
              min={0}
              max={1}
              value={numberValue('opacity')}
              onChange={e => handleNumberChange('opacity', e.target.value)}
              placeholder="0-1"
              className="h-7 w-40 px-2 text-xs"
            />
          </FieldRow>

          <FieldRow label="boxShadow">
            <Input
              type="text"
              value={stringValue('boxShadow')}
              onChange={e => handleStringChange('boxShadow', e.target.value)}
              placeholder="0 2px 8px rgba(0,0,0,.1)"
              className="h-7 w-40 px-2 text-xs"
            />
          </FieldRow>
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-xs font-medium text-muted-foreground">
          Text{' '}
          <span className="text-[10px] font-normal text-muted-foreground/70">
            · styles the text block only
          </span>
        </h4>
        <div className="space-y-2">
          {TEXT_COLOR_FIELDS.map(f => (
            <FieldRow key={f.key} label={f.label}>
              <input
                type="color"
                aria-label={`text ${f.label} swatch`}
                value={toHexOrDefault(textFields[f.key])}
                onChange={e => handleTextStringChange(f.key, e.target.value)}
                className="h-7 w-12 shrink-0 cursor-pointer rounded border border-input p-0.5"
              />
              <Input
                type="text"
                value={textStringValue(f.key)}
                onChange={e => handleTextStringChange(f.key, e.target.value)}
                placeholder="unset"
                className="h-7 w-[6.5rem] px-2 text-xs"
              />
            </FieldRow>
          ))}

          <div>
            <span className="mb-1.5 block text-xs text-muted-foreground">
              Padding{' '}
              <span className="font-normal text-muted-foreground/70">(px)</span>
            </span>
            <SideInputsRow
              fields={TEXT_PADDING_SIDE_FIELDS}
              getValue={textNumberValue}
              onValueChange={handleTextNumberChange}
            />
          </div>

          <FieldRow label="borderRadius">
            <Input
              type="number"
              value={textNumberValue('borderRadius')}
              onChange={e =>
                handleTextNumberChange('borderRadius', e.target.value)
              }
              className="h-7 w-40 px-2 text-xs"
            />
          </FieldRow>
        </div>
      </div>
    </div>
  )
}
